import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, AlertTriangle, Scale, Percent, Radar, ArrowRight, CircleDollarSign } from 'lucide-react';
import { KpiTile } from '@/components/common/KpiTile';
import { ConfidenceBar } from '@/components/common/StatusBadge';
import { ClassificationBadge } from '@/components/opportunities/OppBadges';
import { KpiRowSkeleton, TableSkeleton } from '@/components/common/Skeletons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import api from '@/lib/api';
import { fmtMoney, fmtRelative } from '@/lib/format';

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/analytics/recovery').then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <KpiRowSkeleton />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6"><div className="lg:col-span-2"><TableSkeleton /></div><TableSkeleton rows={5} /></div>
      </div>
    );
  }
  if (!data) return null;
  const k = data.kpis;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Portfolio-wide revenue recovery across <span className="font-semibold text-slate-700">{data.project_count}</span> active projects</p>
        <Button onClick={() => navigate('/detections')} data-testid="go-detections-btn" className="group">
          <Radar className="h-4 w-4" /> Review {k.pending_detections} pending detection{k.pending_detections === 1 ? '' : 's'}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiTile testid="kpi-secured" label="Secured" value={k.secured} variant="recovered" icon={TrendingUp}
          meta={`${fmtMoney(k.recovered)} paid · rest approved & invoiced`} />
        <KpiTile testid="kpi-at-risk" label="At Risk" value={k.at_risk} variant="at_risk" icon={AlertTriangle}
          meta={`${k.pending_detections} open detection${k.pending_detections === 1 ? '' : 's'} + drafted/submitted COs`} />
        <KpiTile testid="kpi-disputed" label="In Dispute" value={k.in_dispute} variant="disputed" icon={Scale}
          meta="Currently in negotiation" />
        <KpiTile testid="kpi-recovery-rate" label="Recovery Rate" value={k.recovery_rate} variant="info" icon={Percent}
          isMoney={false} suffix="%" meta={`${fmtMoney(k.secured)} secured of ${fmtMoney(k.identified)} identified`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-0 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#ECEEF2]">
            <h3 className="text-sm font-semibold text-ink-900">Pending Detections &mdash; Action Required</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/detections')} data-testid="view-all-detections" className="text-brand hover:text-brand-600">
              View all <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
          <div className="divide-y divide-[#F1F2F5]">
            {data.pending.length === 0 && <div className="px-5 py-12 text-center text-sm text-slate-500">All detections actioned. Nice work.</div>}
            {data.pending.map((d) => (
              <button key={d.id} onClick={() => navigate('/detections')} data-testid={`pending-${d.id}`}
                className="w-full text-left px-5 py-3.5 row-hover flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-ink-900 truncate">{d.title}</div>
                  <div className="text-xs text-slate-500 truncate">{d.project_name} &middot; {d.trade}</div>
                </div>
                <ConfidenceBar value={d.confidence} />
                <div className="text-right w-28 shrink-0">
                  <ClassificationBadge classification={d.classification} />
                </div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-[#ECEEF2]"><h3 className="text-sm font-semibold text-ink-900">Recent Activity</h3></div>
          <div className="divide-y divide-[#F1F2F5] max-h-[440px] overflow-y-auto">
            {data.recent_activity.map((a, i) => (
              <div key={i} className="px-5 py-3 flex items-start gap-3">
                <div className={`mt-0.5 rounded-lg p-1.5 ${a.type === 'detection' ? 'bg-warn-50' : 'bg-money-50'}`}>
                  {a.type === 'detection' ? <Radar className="h-3.5 w-3.5 text-warn-700" /> : <CircleDollarSign className="h-3.5 w-3.5 text-money-700" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-slate-700">
                    {a.type === 'detection'
                      ? <>Detected <span className="font-semibold text-ink-900">{a.title}</span></>
                      : <><span className="font-semibold text-ink-900">{a.co_number}</span> &rarr; {a.status}</>}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {a.type === 'detection' ? null : <><span className="num">{fmtMoney(a.value)}</span> &middot; </>}{fmtRelative(a.at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
